import { useState, useEffect, useRef } from 'react';
import { Heading, Paragraph, Label, Input, Button } from '@/components/atoms';
import { InputOTP, InputOTPGroup, InputOTPSlot } from '@/components/atoms';
import { TwoFactorDialogWrapper } from './two-factor-dialog-wrapper';
import { useSetupTwoFactor, useVerifyTwoFactor } from '@/api';

interface EmailOTPVerificationProps {
  isOpen: boolean;
  onClose: () => void;
  onBack: () => void;
  onSuccess: () => void;
  email: string;
}

export const EmailOTPVerification = ({
  isOpen,
  onClose,
  onBack,
  onSuccess,
  email,
}: EmailOTPVerificationProps) => {
  const [otp, setOtp] = useState('');
  const [error, setError] = useState('');
  const [timer, setTimer] = useState(60);
  const [canResend, setCanResend] = useState(false);

  const { mutate: setupTwoFactor, isPending: isSettingUp } =
    useSetupTwoFactor();

  const { mutate: verifyTwoFactor, isPending: isVerifying } =
    useVerifyTwoFactor(() => {
      onSuccess();
    });

  const initialized = useRef(false);

  useEffect(() => {
    if (isOpen && !initialized.current) {
      initialized.current = true;
      setupTwoFactor({ method: 'email' });
      setTimer(60);
      setCanResend(false);
    }
  }, [isOpen, setupTwoFactor]);

  useEffect(() => {
    if (timer > 0) {
      const countdown = setTimeout(() => setTimer(timer - 1), 1000);
      return () => clearTimeout(countdown);
    } else {
      setCanResend(true);
    }
  }, [timer]);

  const handleVerify = () => {
    if (otp.length !== 6) {
      setError('Invalid code. please try again');
      return;
    }
    verifyTwoFactor({ token: otp });
  };

  const handleResend = () => {
    if (canResend) {
      setupTwoFactor({ method: 'email' });
      setTimer(60);
      setCanResend(false);
      setOtp('');
      setError('');
    }
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}.${secs.toString().padStart(2, '0')}`;
  };

  return (
    <TwoFactorDialogWrapper
      isOpen={isOpen}
      onClose={onClose}
      title="Set up Two-Factor Authentication"
      onBack={onBack}
      onContinue={handleVerify}
      continueLabel="Verify & Continue"
      continueDisabled={otp.length !== 6 || isSettingUp}
      isLoading={isVerifying}
      showBackButton
    >
      <div className="flex flex-col gap-6">
        <div>
          <Heading size="lg" className="text-gray-900 mb-2">
            Email OTP
          </Heading>
          <Paragraph className="text-sm text-gray-600">
            Receive codes via email. Backup option, less secure if email is
            compromised
          </Paragraph>
        </div>

        <div className="flex flex-col gap-2">
          <Label htmlFor="email-address" className="text-sm font-medium">
            Email address
          </Label>
          <div className="relative">
            <Input
              id="email-address"
              type="email"
              value={email}
              disabled
              className="bg-gray-50"
            />
            {!isSettingUp && (
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs bg-green-100 text-green-700 px-2 py-1 rounded">
                Code sent! Check your inbox
              </span>
            )}
            {isSettingUp && (
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded">
                Sending...
              </span>
            )}
          </div>
        </div>

        <div className="flex flex-col gap-3">
          <Label className="text-sm font-medium">
            Enter 6-digit code
            {error && (
              <span className="text-red-500 text-xs ml-auto float-right">
                {error}
              </span>
            )}
          </Label>
          <InputOTP
            maxLength={6}
            value={otp}
            onChange={(value) => {
              setOtp(value);
              setError('');
            }}
          >
            <InputOTPGroup className="gap-2 justify-center">
              {Array.from({ length: 6 }).map((_, index) => (
                <InputOTPSlot
                  key={index}
                  index={index}
                  className={error ? 'border-red-500 bg-red-50' : ''}
                />
              ))}
            </InputOTPGroup>
          </InputOTP>
        </div>

        <div className="flex justify-between items-center">
          <Button
            onClick={handleResend}
            disabled={!canResend || isSettingUp}
            variant="ghost"
            className="text-sm font-medium p-0 h-auto"
          >
            Resend code
          </Button>
          <span className="text-sm text-gray-600">{formatTime(timer)}</span>
        </div>
      </div>
    </TwoFactorDialogWrapper>
  );
};
